import { type JSX, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Card } from '@/components/ui';
import { Icon, type IconComponent } from '@/components/icons';
import { cn } from '@/lib/utils';

/**
 * CampaignChannelChooser — the fork the "New campaign" button opens for
 * tenants that have the voice module enabled:
 *
 *   SMS campaign   → the current SMS new-campaign destination (`smsTo`)
 *   Voice campaign → the voice campaign creation entry (`voiceTo`,
 *                    defaults to the /voice/campaigns hub which owns its
 *                    own Basic/Advanced fork)
 *
 * SMS-only tenants never mount this — their button navigates straight to
 * the SMS flow, exactly as before. Built on the same overlay skeleton as
 * ConfirmModal / CampaignModeChooser (the app's only modal primitive),
 * with two large Card choices. Esc and backdrop click close it.
 */
export interface CampaignChannelChooserProps {
  open: boolean;
  onClose: () => void;
  /** Where the SMS card routes — the current "New campaign" destination. */
  smsTo: string;
  /** Where the Voice card routes. Defaults to the voice campaigns hub. */
  voiceTo?: string;
}

export function CampaignChannelChooser({
  open,
  onClose,
  smsTo,
  voiceTo = '/voice/campaigns',
}: CampaignChannelChooserProps): JSX.Element | null {
  const { t } = useTranslation('default');
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-ink/50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="campaign-channel-chooser-title"
      onClick={onClose}
    >
      <div
        className="bg-surface border border-line rounded-lg shadow-lg w-full max-w-[640px] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-5 py-4 border-b border-line">
          <h2
            id="campaign-channel-chooser-title"
            className="display text-[16px] font-semibold text-text"
          >
            {t('campaign_chooser_title')}
          </h2>
          <p className="text-[13px] text-text-3 mt-1">{t('campaign_chooser_subtitle')}</p>
        </div>
        <div className="px-5 py-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ChannelCard
            accent="sms"
            icon={Icon.chat}
            title={t('campaign_chooser_sms_title')}
            description={t('campaign_chooser_sms_desc')}
            onClick={() => navigate(smsTo)}
          />
          <ChannelCard
            accent="voice"
            icon={Icon.phone}
            title={t('campaign_chooser_voice_title')}
            description={t('campaign_chooser_voice_desc')}
            onClick={() => navigate(voiceTo)}
          />
        </div>
      </div>
    </div>
  );
}

type ChannelAccent = 'sms' | 'voice';

// Static class strings per accent — Tailwind can't see dynamically built
// class names, so each variant is spelled out in full.
const ACCENT: Record<
  ChannelAccent,
  { card: string; iconWrap: string; icon: string }
> = {
  sms: {
    card: 'hover:border-volt hover:bg-volt-50 focus:border-volt focus:ring-volt-glow',
    iconWrap: 'bg-volt-100 border-volt-200',
    icon: 'text-volt-700',
  },
  voice: {
    card: 'hover:border-voice hover:bg-voice-soft focus:border-voice focus:ring-voice-soft',
    iconWrap: 'bg-voice-soft border-voice-bd',
    icon: 'text-voice',
  },
};

function ChannelCard({
  accent,
  icon: IconGlyph,
  title,
  description,
  onClick,
}: {
  accent: ChannelAccent;
  icon: IconComponent;
  title: string;
  description: string;
  onClick: () => void;
}): JSX.Element {
  const styles = ACCENT[accent];
  return (
    <Card
      padding="lg"
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'cursor-pointer transition-colors border border-line-strong',
        'focus:outline-none focus:ring-[3px]',
        styles.card,
      )}
    >
      <div
        className={cn(
          'w-10 h-10 grid place-items-center rounded-md border mb-3',
          styles.iconWrap,
        )}
        aria-hidden="true"
      >
        <IconGlyph width="20" height="20" className={styles.icon} />
      </div>
      <h3 className="display text-[15px] font-semibold text-text mb-1.5">{title}</h3>
      <p className="text-[13px] text-text-3 leading-relaxed">{description}</p>
    </Card>
  );
}
